// utils.ts
// Utility functions for data processing

export const asHttpImageOrNull = (value?: any): string | null =>
    typeof value === "string" && /^https?:\/\//i.test(value) ? value : null;

// Analytics data point type
export type AnalyticsDataPoint = {
    key: string;
    date: string;
    dateISO: string;
    sent: number;
    delivered: number;
    opened: number;
    unsubscribed: number;
};

// Get date range for analytics filters
export function getDateRange(range: string): { startDate: Date; endDate: Date } {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    
    if (range === "7d") {
        startDate.setDate(startDate.getDate() - 7);
    } else if (range === "90d") {
        startDate.setDate(startDate.getDate() - 90);
    } else {
        // Default to 30 days
        startDate.setDate(startDate.getDate() - 30);
    }
    
    return { startDate, endDate };
}

// Client-side aggregation for bar chart (in case we need it)
export function aggregate(series: AnalyticsDataPoint[], mode: "daily" | "weekly" | "monthly"): AnalyticsDataPoint[] {
    if (mode === "daily") return series.map((d) => ({ ...d, key: d.date }));
    
    const buckets: Record<string, AnalyticsDataPoint> = {};
    for (const d of series) {
        const date = new Date(d.dateISO);
        const key =
            mode === "weekly"
                ? `Week ${Math.ceil(date.getDate() / 7)}`
                : date.toLocaleDateString("en-US", { month: "short" });
        if (!buckets[key]) {
            buckets[key] = { 
                key, 
                date: key,
                dateISO: d.dateISO,
                sent: 0, 
                delivered: 0, 
                opened: 0, 
                unsubscribed: 0 
            };
        }
        buckets[key].sent += d.sent;
        buckets[key].delivered += d.delivered;
        buckets[key].opened += d.opened;
        buckets[key].unsubscribed += d.unsubscribed;
    }
    return Object.values(buckets);
}

export function extractPlaceholdersFromTemplate(tpl: {
    title?: string;
    subtitle?: string | null;
    body?: string;
}) {
    const rx = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
    const text = `${tpl.title ?? ""}\n${tpl.subtitle ?? ""}\n${tpl.body ?? ""}`;
    const set = new Set<string>();
    let m: RegExpExecArray | null;
    while ((m = rx.exec(text))) set.add(m[1]);
    return Array.from(set);
}

export function parseCSV(text: string): {
    headers: string[];
    rows: Record<string, string>[];
} {
    function splitCSVLine(line: string): string[] {
        const result: string[] = [];
        let cur = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    cur += '"';
                    i++;
                } else inQuotes = !inQuotes;
            } else if (ch === "," && !inQuotes) {
                result.push(cur.trim());
                cur = "";
            } else {
                cur += ch;
            }
        }
        result.push(cur.trim());
        return result.map((v) =>
            v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1) : v
        );
    }

    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length === 0) return { headers: [], rows: [] };
    
    const header = splitCSVLine(lines[0]).map((h) => h.trim());
    const rows: Record<string, string>[] = [];
    
    for (let i = 1; i < lines.length; i++) {
        const cols = splitCSVLine(lines[i]);
        const row: Record<string, string> = {};
        for (let j = 0; j < header.length; j++) {
            row[header[j]] = (cols[j] ?? "").trim();
        }
        rows.push(row);
    }
    
    return { headers: header, rows };
}
